import { categoriesSelectors } from 'app-components/flux/categories';
import type { Business } from 'app-components/flux/business/businessTypes';
import type { ChartOfAccountNode } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { customersSelectors } from 'app-components/flux/customers';
import type { AdvancedFilter, CustomerAndProjects, FilterObjectProps } from 'app-components/flux/filters/filtersTypes';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Project } from 'app-components/flux/projects/projectsTypes';
import { mkProject } from 'app-components/flux/projects/projectsTypes';
import store from 'app-components/utils/redux-store';
import {
  CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
  DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION,
  QCS_ID_FOR_CLIENT_ONLY_UI_OPTION,
  VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION,
} from 'components/QFilterSelect/constants';
import { flagFieldOptions } from 'data/transactions/config';
import { Map as ImmutableMap, type List, type Set as ImmutableSet } from 'immutable';
import { exists } from 'utils/utils';

import type { FilterItem } from './componentTypes';

import ClientIcon from 'assets/icons/nav-icons/clients.inline.svg';
import BusinessIcon from 'assets/icons/settings-menu/business.inline.svg';
import AccountIcon from 'assets/icons/settings-menu/accounts.settings.simplifi.inline.svg';
import CategoryIcon from 'assets/icons/settings-menu/categories.tags.settings.simplifi.inline.svg';
import FlagIcon from 'assets/icons/settings-menu/flags.inline.svg';
import PayeeIcon from 'assets/icons/settings-menu/payee-store-front.inline.svg';
import AdvancedIcon from 'assets/icons/settings-menu/renaming.rules.settings.simplifi.inline.svg';
import TagIcon from 'assets/icons/settings-menu/tag-icon.inline.svg';

export const filterResourceTitles = {
  categories: 'Categories',
  businesses: 'Business & personal',
  payeeNames: 'Payees',
  tags: 'Tags',
  accounts: 'Accounts',
  [CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE]: 'Clients & Projects',
  flags: 'Flags',
  advanced: 'Advanced',
} as const;

export type FilterResourceTitleType = typeof filterResourceTitles;

export const itemMenuConfig: {
  resourceType: keyof FilterResourceTitleType;
  title: FilterResourceTitleType[keyof FilterResourceTitleType];
  id: string;
  icon: typeof CategoryIcon;
}[] = [
  {
    resourceType: 'categories',
    title: filterResourceTitles.categories,
    id: 'add_filter_button_category',
    icon: CategoryIcon,
  },
  {
    resourceType: 'businesses',
    title: filterResourceTitles.businesses,
    id: 'add_filter_button_usage',
    icon: BusinessIcon,
  },
  {
    resourceType: 'payeeNames',
    title: filterResourceTitles.payeeNames,
    id: 'add_filter_button_payee',
    icon: PayeeIcon,
  },
  {
    resourceType: 'tags',
    title: filterResourceTitles.tags,
    id: 'add_filter_button_tag',
    icon: TagIcon,
  },
  {
    resourceType: 'accounts',
    title: filterResourceTitles.accounts,
    id: 'add_filter_button_account',
    icon: AccountIcon,
  },
  {
    resourceType: CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
    title: filterResourceTitles[CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE],
    id: 'add_filter_button_clients_and_projects',
    icon: ClientIcon,
  },
  {
    resourceType: 'flags',
    title: filterResourceTitles.flags,
    id: 'add_filter_button_flag',
    icon: FlagIcon,
  },
  {
    resourceType: 'advanced',
    title: filterResourceTitles.advanced,
    id: 'add_filter_button_advanced',
    icon: AdvancedIcon,
  },
];

const countDefinedItems = (imap?: ImmutableMap<string, unknown>): number => {
  if (!imap) return 0;
  return imap.reduce((v: number, x) => v + (exists(x) ? 1 : 0), 0);
};

// Of a filter's fields, only the sets have a size; the others count as 0.
type MaybeSized = { size?: number } | null | undefined;

export const getCustomerIdOfSelectedProject = (selectedProjectId: string): string | undefined => {
  if (!store) {
    return;
  }
  if (selectedProjectId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
    const items = selectedProjectId.split(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION);
    return items[0];
  } else {
    const projects = projectsSelectors.getProjects(store.getState());
    const projectObj = projects.get(selectedProjectId);
    return projectObj?.customerId;
  }
};

export const getCustomersAndProjectsSelectedFromSelectedIds = (
  selectedIds: string[] | ImmutableSet<string>,
): CustomerAndProjects[] => {
  const selectedIdList = Array.isArray(selectedIds)
    ? selectedIds
    : 'toArray' in selectedIds
      ? selectedIds.toArray()
      : [];
  if (!store) {
    return [];
  }
  const customersById = customersSelectors.getCustomers(store.getState());
  const projectsByCustomerId = projectsSelectors.getProjectsByCustomerId(store.getState());

  const customerIds: string[] = [];
  const projectIds: string[] = [];
  for (const id of selectedIdList) {
    if (customersById.has(id)) {
      customerIds.push(id);
    } else {
      projectIds.push(id);
    }
  }

  const customersAndProjectsSelected: CustomerAndProjects[] = [];

  for (const customerId of customerIds) {
    const allProjects = projectsByCustomerId.get(customerId)?.allProjects;
    const customer = customersById.get(customerId);
    if (customer && customer.id) {
      if (!allProjects) {
        customersAndProjectsSelected.push({
          customer: customer,
          projects: [new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION })],
        });
      } else {
        const isAnyProjectSelected = allProjects.some((project: Project) => projectIds.includes(project.id || ''));
        if (!isAnyProjectSelected) {
          customersAndProjectsSelected.push({
            customer: customer,
            projects: [new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION }), ...allProjects],
          });
        }
      }
    }
  }

  for (const projectId of projectIds) {
    const customerId = getCustomerIdOfSelectedProject(projectId);
    const customerExist = customersAndProjectsSelected.find((item) => item?.customer?.id === customerId);
    if (!customerExist) {
      const customer = customerId ? customersById.get(customerId) : undefined;
      if (customer) customersAndProjectsSelected.push({ customer, projects: [] });
    }
    const objIndex = customersAndProjectsSelected.findIndex((item) => item?.customer?.id === customerId);
    if (projectId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
      customersAndProjectsSelected[objIndex].projects.push(new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION }));
    } else {
      const project = projectsSelectors.getProjectById(store.getState(), projectId) as Project;
      customersAndProjectsSelected[objIndex].projects.push(project);
    }
  }
  return customersAndProjectsSelected;
};

const getClientsAndProjectsFilterItemsCount = (filterObject: FilterObjectProps): number => {
  const selectedClientsAndProjectsInfo = filterObject.clientsAndProjects;
  if (!store || !selectedClientsAndProjectsInfo) return 0;

  const customersById = customersSelectors.getCustomers(store.getState());
  let count = 0;

  for (const selectedId of selectedClientsAndProjectsInfo) {
    if (customersById?.has(selectedId)) {
      count += 1;
    } else {
      const customerId = getCustomerIdOfSelectedProject(selectedId);
      if (customerId && !selectedClientsAndProjectsInfo.includes(customerId)) {
        count += 1;
      }
    }
  }
  return count;
};

export const countFilterItems = (filterObject: FilterObjectProps): number => {
  if (!filterObject) return 0;

  const count =
    ImmutableMap(filterObject)
      .filter((_, k) => k !== 'payees')
      .reduce((v: number, x) => v + ((x as MaybeSized)?.size || 0), 0) || 0;
  const objToCount = {
    // Make sure to count other fields not covered by this filter
    // TODO: we should have a more uniform way of doing this
    ...filterObject.advanced?.toObject?.(),
    dateStart: filterObject.dateStart,
    dateEnd: filterObject.dateEnd,
  };
  return count + countDefinedItems(ImmutableMap(objToCount));
};

type CountForDisplayOptions = {
  /**
   * Reports keep `businesses` in a separate Usage control, so it is not counted by default.
   * Register filters, which offer Business & personal as a filter, opt in.
   */
  includeBusinesses?: boolean;
};

export const countFilterItemsForDisplay = (
  filterObject: FilterObjectProps,
  { includeBusinesses = false }: CountForDisplayOptions = {},
): number => {
  if (!filterObject) return 0;

  let count =
    ImmutableMap(filterObject)
      .filter(
        (_, k) =>
          k !== 'payees' &&
          (includeBusinesses || k !== 'businesses') &&
          k !== 'categories' &&
          k !== CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE &&
          k !== 'customersAndProjectsSelected',
      )
      .reduce((v: number, x) => v + ((x as MaybeSized)?.size || 0), 0) || 0;

  count += getClientsAndProjectsFilterItemsCount(filterObject);

  count +=
    filterObject.categories?.reduce((accum: number, catId: string) => {
      const category = store && categoriesSelectors.getCategoryForId(store.getState(), catId);
      const hasParent = category && category.parentId && category.parentId !== '0';
      const parentIsSelected = hasParent && filterObject?.categories?.has(category.parentId);
      return accum + (parentIsSelected ? 0 : 1);
    }, 0) || 0;

  count += countDefinedItems(ImmutableMap(filterObject?.advanced?.toObject?.() ?? {}));
  return count;
};

export const advancedKeyToName: Record<keyof AdvancedFilter, string> = {
  isBillOrSubscription: 'Bills & Subscriptions',
  isExcludedFromF2S: 'Excluded in spending plan',
  isExcludedFromReports: 'Excluded in reports',
  isReviewed: 'Reviewed',
};

const isKeyAdvancedFilter = (key: unknown): key is keyof typeof advancedKeyToName =>
  typeof key === 'string' && key in advancedKeyToName;

const getNameForClientsAndProjects = (searchItemId: string): string => {
  let customerName = '';
  if (!store) return customerName;

  const customersById = customersSelectors.getCustomers(store.getState());

  if (customersById?.has(searchItemId)) {
    const customer = customersById.get(searchItemId);
    customerName = customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
    return customerName;
  }

  if (searchItemId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
    const items = searchItemId.split(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION);
    const clientOnlyUIOptionCustomerId = items[0];
    if (customersById.has(clientOnlyUIOptionCustomerId)) {
      const customer = customersById.get(clientOnlyUIOptionCustomerId);
      customerName = customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
      return `${customerName}: ${DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION}`;
    } else {
      return 'error';
    }
  }
  const projectsByCustomerId = projectsSelectors.getProjectsByCustomerId(store.getState());

  for (const [customerId, customer] of customersById) {
    const projects = projectsByCustomerId.get(customerId)?.allProjects;
    if (projects) {
      for (const project of projects) {
        if (project?.id === searchItemId) {
          customerName =
            customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
          return `${customerName}: ${project.name}`;
        }
      }
    }
  }
  return 'error';
};

const getNameForBusiness = (businessesById: ImmutableMap<string, Business> | undefined, businessId: string): string =>
  businessId === 'PERSONAL' ? 'Personal' : businessesById?.get(businessId)?.name || 'Unknown business';

// The name lookups only read each resource's name.
type NamedById = ImmutableMap<string, { name?: string | null }>;

const getCategoryName = (categoriesById: NamedById, it: string, accountsById: NamedById): string => {
  switch (it) {
    case 'BALANCE_ADJUSTMENT_0':
      return 'Balance Adjustment';
    case 'BALANCE_ADJUSTMENT_1':
      return 'Opening Balance';
    case 'BALANCE_ADJUSTMENT_2':
      return 'Transfer';
    case 'BALANCE_ADJUSTMENT_3':
      return 'Credit Card Payment';
    case '0':
    case 'UNCATEGORIZED':
      return 'Uncategorized';
    case 'OTHER':
      return 'Other';
    case 'TRANSFER':
      return 'Transfer';
    case 'USAGE-PERSONAL':
      return 'Personal';
    case 'USAGE-BUSINESS':
      return 'Business';
    default:
      if (categoriesById?.has(it)) {
        return categoriesById.get(it)?.name || '';
      }
      if (accountsById?.has(it)) {
        return `Transfer: ${accountsById.get(it)?.name || ''}`;
      }
      return 'error';
  }
};

export const getNameFromKeyFactory =
  (
    categoriesById: NamedById,
    payeesByName: ImmutableMap<string, string>,
    tagsById: NamedById,
    accountsById: NamedById,
    chartOfAccounts: List<ChartOfAccountNode> | undefined,
    businessesById?: ImmutableMap<string, Business>,
  ) =>
  (itemKey: string, item: FilterItem): string => {
    // The advanced filter's items are { key, val } objects; every other filter's items are ids.
    if (typeof item === 'object' && item !== null) {
      const { key } = item;
      return itemKey === 'advanced' && isKeyAdvancedFilter(key)
        ? `${!item.val ? 'Not' : ''} ${advancedKeyToName[key]}`
        : '';
    }
    switch (itemKey) {
      case 'categories':
        return (
          chartOfAccounts?.find((coa) => item === coa.id)?.name || getCategoryName(categoriesById, item, accountsById)
        );
      case 'businesses':
        return getNameForBusiness(businessesById, item);
      case CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE:
        return getNameForClientsAndProjects(item);
      case 'payees':
        return payeesByName.get(item?.toLowerCase()) || '';
      case 'tags':
        return tagsById.get(item)?.name || '';
      case 'accounts':
        return accountsById.get(item)?.name || '';
      case 'flags':
        return flagFieldOptions.find(({ value }) => value === item)?.label || '';
      case 'advanced':
        return '';
      default:
        return 'error';
    }
  };
